import { useEffect, useMemo, useState } from "react";
import { Clock } from "lucide-react";

const pad = (n: number) => String(n).padStart(2, "0");

export function useCountdown(deadline: string | null, serverNow: string) {
  const offset = useMemo(() => new Date(serverNow).getTime() - Date.now(), [serverNow]);
  const [now, setNow] = useState(() => Date.now() + offset);
  useEffect(() => {
    if (!deadline) return;
    const t = setInterval(() => setNow(Date.now() + offset), 1000);
    return () => clearInterval(t);
  }, [deadline, offset]);
  if (!deadline) return null;
  const ms = Math.max(0, new Date(deadline).getTime() - now);
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  return { ms, label: `${d ? `${d}d ` : ""}${pad(Math.floor((s % 86400) / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}` };
}

const fmt = (iso: string, tz?: string) => new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: tz });

export function deadlineText(iso: string) {
  const local = fmt(iso);
  const uk = fmt(iso, "Europe/London");
  return local === uk ? `${uk} UK time` : `${local} your time (${uk} UK)`;
}

export function Countdown({ deadline, serverNow }: { deadline: string; serverNow: string }) {
  const c = useCountdown(deadline, serverNow);
  if (!c) return null;
  const urgent = c.ms < 3600_000;
  return (
    <div className={`inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-xl border-2 border-[#0B1F33] px-2.5 py-1 text-[#0B1F33] shadow-[0_2px_0_#0B1F33] ${urgent ? "bg-[#FF5D5D] text-white" : "bg-white"}`} data-testid="lms-countdown">
      <Clock className="h-4 w-4" strokeWidth={3} />
      <span className="text-[11px] font-extrabold uppercase">Picks close in</span>
      <span className="font-comic text-lg tabular-nums leading-none" data-testid="lms-countdown-value">{c.ms ? c.label : "Closing…"}</span>
      <span className="w-full text-[10px] font-bold opacity-75 sm:w-auto" data-testid="lms-deadline-text">{deadlineText(deadline)}</span>
    </div>
  );
}

function nextFriday() {
  const d = new Date();
  d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7 || 7));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function useDeadlineInput() {
  const [on, setOn] = useState(true);
  const [date, setDate] = useState(nextFriday);
  const [time, setTime] = useState("19:00");
  return { on, setOn, date, setDate, time, setTime, value: on && date && time ? `${date}T${time}` : null };
}

export function DeadlineField({ s }: { s: ReturnType<typeof useDeadlineInput> }) {
  return (
    <div className="space-y-1.5 rounded-xl bg-white/10 p-2" data-testid="lms-deadline-field">
      <label className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-[#FFE14D]">
        <input type="checkbox" className="h-4 w-4 accent-[#35E07A]" checked={s.on} onChange={(e) => s.setOn(e.target.checked)} data-testid="lms-deadline-toggle" />
        Auto-close picks at (UK time)
      </label>
      {s.on && (
        <div className="flex gap-1.5">
          <input type="date" className="field !py-2 !text-sm !text-[#0B1F33]" value={s.date} onChange={(e) => s.setDate(e.target.value)} data-testid="lms-deadline-date" />
          <input type="time" className="field !w-32 !py-2 !text-sm !text-[#0B1F33]" value={s.time} onChange={(e) => s.setTime(e.target.value)} data-testid="lms-deadline-time" />
        </div>
      )}
    </div>
  );
}
